import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { AlertCircle, CheckCircle, Upload } from 'lucide-react';
import FileUpload from '../components/FileUpload';
import { removeExifData } from '../utils/imageUtils';
import './DenunciaForm.css';

const DELITOS = [
  'Fraude',
  'Apropiación Indebida',
  'Soborno o Corrupción',
  'Uso de información privilegiada',
  'Conflicto de Intereses',
  'Delitos Tributarios',
  'Otros'
];

const DenunciaForm = () => {
  const [formData, setFormData] = useState({
    delitos: [],
    delito_otro_detalle: '',
    detalle_hechos: {
      que: '',
      cuando: '',
      donde: '',
      como: ''
    },
    involucrados: {
      infractores: '',
      victimas: '',
      testigos: ''
    },
    denunciante: {
      nombre: '',
      email: ''
    },
    declaracion_aceptada: false
  });

  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleDelitoChange = (delito) => {
    setFormData(prev => {
      const delitos = prev.delitos.includes(delito)
        ? prev.delitos.filter(d => d !== delito)
        : [...prev.delitos, delito];
      return { ...prev, delitos };
    });
    if (errors.delitos) {
      setErrors(prev => ({ ...prev, delitos: '' }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
    if (errors[`${parent}.${field}`]) {
      setErrors(prev => ({ ...prev, [`${parent}.${field}`]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.delitos.length === 0) {
      newErrors.delitos = 'Debe seleccionar al menos un tipo de delito';
    }

    if (formData.delitos.includes('Otros') && !formData.delito_otro_detalle.trim()) {
      newErrors.delito_otro_detalle = 'Debe especificar el tipo de delito';
    }

    if (!formData.detalle_hechos.que.trim()) {
      newErrors['detalle_hechos.que'] = 'Este campo es obligatorio';
    }
    if (!formData.detalle_hechos.cuando.trim()) {
      newErrors['detalle_hechos.cuando'] = 'Este campo es obligatorio';
    }
    if (!formData.detalle_hechos.donde.trim()) {
      newErrors['detalle_hechos.donde'] = 'Este campo es obligatorio';
    }
    if (!formData.detalle_hechos.como.trim()) {
      newErrors['detalle_hechos.como'] = 'Este campo es obligatorio';
    }

    if (!formData.involucrados.infractores.trim()) {
      newErrors['involucrados.infractores'] = 'Este campo es obligatorio';
    }

    if (!formData.declaracion_aceptada) {
      newErrors.declaracion_aceptada = 'Debe aceptar la declaración de veracidad';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadFiles = async () => {
    const uploadPromises = files.map(async (file) => {
      const cleanedFile = await removeExifData(file);
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `evidencias/${fileName}`);
      
      await uploadBytes(storageRef, cleanedFile);
      return await getDownloadURL(storageRef);
    });

    return await Promise.all(uploadPromises);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const evidenciasUrls = files.length > 0 ? await uploadFiles() : [];

      const denunciaData = {
        created_at: serverTimestamp(),
        status: 'Nueva',
        delitos: formData.delitos,
        delito_otro_detalle: formData.delitos.includes('Otros') ? formData.delito_otro_detalle : '',
        detalle_hechos: formData.detalle_hechos,
        involucrados: formData.involucrados,
        evidencias_urls: evidenciasUrls,
        denunciante: {
          es_anonimo: !formData.denunciante.nombre && !formData.denunciante.email,
          nombre: formData.denunciante.nombre || null,
          email: formData.denunciante.email || null
        },
        declaracion_aceptada: formData.declaracion_aceptada
      };

      await addDoc(collection(db, 'denuncias'), denunciaData);

      setSubmitSuccess(true);
      setFormData({
        delitos: [],
        delito_otro_detalle: '',
        detalle_hechos: { que: '', cuando: '', donde: '', como: '' },
        involucrados: { infractores: '', victimas: '', testigos: '' },
        denunciante: { nombre: '', email: '' },
        declaracion_aceptada: false
      });
      setFiles([]);

    } catch (error) {
      console.error('Error al enviar denuncia:', error);
      setSubmitError('Ocurrió un error al enviar la denuncia. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="container">
        <div className="success-card card">
          <div className="success-icon">
            <CheckCircle size={80} color="#16A34A" />
          </div>
          <h2>¡Denuncia Registrada Exitosamente!</h2>
          <div className="success-message">
            <p className="message-primary">
              Su denuncia ha sido recibida y registrada en nuestro sistema. 
              Le agradecemos profundamente el valor y la responsabilidad demostrada al reportar esta situación.
            </p>
            <p className="message-secondary">
              <strong>Confidencialidad garantizada:</strong> Toda la información proporcionada será tratada 
              con la máxima discreción y privacidad. Nuestro equipo especializado revisará su denuncia 
              siguiendo los protocolos establecidos en nuestro Modelo de Prevención de Delitos.
            </p>
            <p className="message-secondary">
              Su contribución es fundamental para mantener un ambiente de trabajo ético, seguro y transparente. 
              Gracias por su confianza en Gauss Control.
            </p>
          </div>
          <button 
            onClick={() => setSubmitSuccess(false)} 
            className="btn btn-primary"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="form-container card">
        <div className="form-header">
          <h2>Formulario de Denuncia Anónima</h2>
          <p>Complete el siguiente formulario para reportar una situación irregular. Su identidad puede permanecer anónima.</p>
        </div>

        <form onSubmit={handleSubmit} className="denuncia-form">
          <section className="form-section">
            <h3>1. Tipo de Delito</h3>
            <p className="section-description">Seleccione uno o más tipos de delito (obligatorio)</p>
            
            <div className="checkbox-group">
              {DELITOS.map(delito => (
                <label key={delito} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.delitos.includes(delito)}
                    onChange={() => handleDelitoChange(delito)}
                  />
                  <span>{delito}</span>
                </label>
              ))}
            </div>
            {errors.delitos && <p className="error-message">{errors.delitos}</p>}

            {formData.delitos.includes('Otros') && (
              <div className="form-group">
                <label>Especifique el tipo de delito *</label>
                <input
                  type="text"
                  value={formData.delito_otro_detalle}
                  onChange={(e) => handleInputChange('delito_otro_detalle', e.target.value)}
                  className={errors.delito_otro_detalle ? 'error' : ''}
                />
                {errors.delito_otro_detalle && <p className="error-message">{errors.delito_otro_detalle}</p>}
              </div>
            )}
          </section>

          <section className="form-section">
            <h3>2. Descripción de los Hechos</h3>
            
            <div className="form-group">
              <label>¿Qué pasó? *</label>
              <textarea
                rows="4"
                value={formData.detalle_hechos.que}
                onChange={(e) => handleNestedChange('detalle_hechos', 'que', e.target.value)}
                placeholder="Describa detalladamente los hechos ocurridos"
                className={errors['detalle_hechos.que'] ? 'error' : ''}
              />
              {errors['detalle_hechos.que'] && <p className="error-message">{errors['detalle_hechos.que']}</p>}
            </div>

            <div className="form-group">
              <label>¿Cuándo pasó? *</label>
              <input
                type="date"
                value={formData.detalle_hechos.cuando}
                onChange={(e) => handleNestedChange('detalle_hechos', 'cuando', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className={errors['detalle_hechos.cuando'] ? 'error' : ''}
              />
              <small className="field-hint">Seleccione la fecha aproximada en que ocurrieron los hechos</small>
              {errors['detalle_hechos.cuando'] && <p className="error-message">{errors['detalle_hechos.cuando']}</p>}
            </div>

            <div className="form-group">
              <label>¿Dónde pasó? *</label>
              <input
                type="text"
                value={formData.detalle_hechos.donde}
                onChange={(e) => handleNestedChange('detalle_hechos', 'donde', e.target.value)}
                placeholder="Ubicación o área donde ocurrieron los hechos"
                className={errors['detalle_hechos.donde'] ? 'error' : ''}
              />
              {errors['detalle_hechos.donde'] && <p className="error-message">{errors['detalle_hechos.donde']}</p>}
            </div>

            <div className="form-group">
              <label>¿Cómo se detectó? *</label>
              <textarea
                rows="3"
                value={formData.detalle_hechos.como}
                onChange={(e) => handleNestedChange('detalle_hechos', 'como', e.target.value)}
                placeholder="Describa cómo se detectó o descubrió la situación"
                className={errors['detalle_hechos.como'] ? 'error' : ''}
              />
              {errors['detalle_hechos.como'] && <p className="error-message">{errors['detalle_hechos.como']}</p>}
            </div>
          </section>

          <section className="form-section">
            <h3>3. Identificación de Involucrados</h3>
            
            <div className="form-group">
              <label>Presunto Infractor *</label>
              <input
                type="text"
                value={formData.involucrados.infractores}
                onChange={(e) => handleNestedChange('involucrados', 'infractores', e.target.value)}
                placeholder="Nombre o descripción de la(s) persona(s) involucrada(s)"
                className={errors['involucrados.infractores'] ? 'error' : ''}
              />
              {errors['involucrados.infractores'] && <p className="error-message">{errors['involucrados.infractores']}</p>}
            </div>

            <div className="form-group">
              <label>Víctima/Afectado (Opcional)</label>
              <input
                type="text"
                value={formData.involucrados.victimas}
                onChange={(e) => handleNestedChange('involucrados', 'victimas', e.target.value)}
                placeholder="Persona(s) afectada(s) por los hechos"
              />
            </div>

            <div className="form-group">
              <label>Posibles Testigos (Opcional)</label>
              <input
                type="text"
                value={formData.involucrados.testigos}
                onChange={(e) => handleNestedChange('involucrados', 'testigos', e.target.value)}
                placeholder="Persona(s) que presenciaron los hechos"
              />
            </div>
          </section>

          <section className="form-section">
            <h3>4. Evidencias</h3>
            <p className="section-description">Adjunte archivos que respalden su denuncia (PDF, JPG, PNG)</p>
            
            <FileUpload files={files} setFiles={setFiles} />
          </section>

          <section className="form-section">
            <h3>5. Identificación del Denunciante (Opcional)</h3>
            <p className="section-description">Puede permanecer anónimo dejando estos campos en blanco</p>
            
            <div className="form-group">
              <label>Nombre</label>
              <input
                type="text"
                value={formData.denunciante.nombre}
                onChange={(e) => handleNestedChange('denunciante', 'nombre', e.target.value)}
                placeholder="Su nombre completo"
              />
            </div>

            <div className="form-group">
              <label>Correo Electrónico</label>
              <input
                type="email"
                value={formData.denunciante.email}
                onChange={(e) => handleNestedChange('denunciante', 'email', e.target.value)}
                placeholder="correo@ejemplo.com"
              />
            </div>
          </section>

          <section className="form-section">
            <h3>6. Declaración de Veracidad</h3>
            
            <div className="declaracion-box">
              <p>
                Declaro bajo juramento que la información proporcionada en este formulario es verdadera y completa según mi mejor conocimiento. 
                Comprendo que proporcionar información falsa puede tener consecuencias legales. Autorizo a Gauss Control a investigar los hechos 
                denunciados y a tomar las medidas que considere necesarias conforme a su Modelo de Prevención de Delitos.
              </p>
            </div>

            <label className="checkbox-label declaracion-checkbox">
              <input
                type="checkbox"
                checked={formData.declaracion_aceptada}
                onChange={(e) => handleInputChange('declaracion_aceptada', e.target.checked)}
              />
              <span>He leído y acepto la declaración de veracidad *</span>
            </label>
            {errors.declaracion_aceptada && <p className="error-message">{errors.declaracion_aceptada}</p>}
          </section>

          {submitError && (
            <div className="alert alert-error">
              <AlertCircle size={20} />
              <span>{submitError}</span>
            </div>
          )}

          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="spinner-small"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Upload size={20} />
                  Enviar Denuncia
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DenunciaForm;
